import { format, type Locale } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { parseLocalYmd } from '@/shared/lib/server-date';
import { Button } from '@/shared/ui/button';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

function toIsoDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface DatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  fromDate?: Date;
  toDate?: Date;
  locale?: Locale;
}

/** Seletor de data (Popover + Calendar) integrado com strings `YYYY-MM-DD`. */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Selecione a data',
  disabled,
  id,
  className,
  fromDate,
  toDate,
  locale = ptBR,
}: DatePickerProps) {
  const selected = parseLocalYmd(value);

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="secondary"
          disabled={disabled}
          className={cn(
            'field-glass h-10 w-full justify-start gap-2 rounded-lg border border-input bg-[hsl(var(--glass-bg)/0.5)] px-3.5 text-left font-normal shadow-glass-sm',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0 opacity-70" />
          <span className="truncate">
            {selected ? format(selected, 'dd/MM/yyyy', { locale }) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[60] w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => onChange(date ? toIsoDateString(date) : '')}
          locale={locale}
          disabled={(date) => {
            if (fromDate && date < fromDate) return true;
            if (toDate && date > toDate) return true;
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
